import React, { useCallback, useEffect, useState, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';

import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Fab, Tab, Tabs, Typography } from '@mui/material';
import useImage from 'services/Hooks/useImage';
import { useParams } from 'react-router';
import { useInventoryItem } from 'services/Data/InventoryItemServices';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import { Add, AttachFile, Remove } from '@mui/icons-material';
import ImagesSlideView from 'Components/ImagesSlideView';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import DimensionTypeEnum from 'enums/DimensionTypeEnum';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { v4 as uuidv4 } from 'uuid';
import { useActiveCart, usePostCarItem } from 'services/Data/CartService';
import FrequentlyBoughtTogetherTypeEnum from 'enums/FrequentlyBoughtTogetherTypeEnum';
import FrequentlyBoughtTogether from '../Components/FrequentlyBoughtTogether';
import { useSelector } from 'react-redux';
import { FileTypeEnumValue } from 'enums/FileTypeEnum';
import { TabContext, TabPanel } from '@mui/lab';
import CartRedirectionModal from '../Components/CartRedirectionModal';
import { IsImpersonationMode } from 'config/config';

const useStyles = makeStyles(theme => ({
  description: {
    '& ul': {
      marginLeft: '2vh',
    },
  },
  tabsList: {
    position: 'relative',
    minHeight: 10,
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.secondary.main,
    },
    '& :hover': {
      color: theme.palette.secondary.main,

      '& >svg': {
        color: theme.palette.secondary.main,
      },
    },
    '& .Mui-selected': {
      color: `${theme.palette.secondary.main}`,
    },
  },
  tabItem: {
    maxWidth: 'none',
    minWidth: 10,
    minHeight: 10,
    paddingLeft: 20,
    paddingRight: 20,
    textTransform: 'none',
    color: theme.palette.common.black,
    fontSize: 18,
    fontWeight: theme.typography.fontWeightRegular,
  },
  fileLink: {
    color: theme.palette.common.black,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
}));
const InventoryItemCategory = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { inventoryItemId: legacyInventoryItemId, dealerPortalMenuProductId } = useParams();
  const inventoryItemId = legacyInventoryItemId || dealerPortalMenuProductId;
  const accountReducer = useSelector(state => state.AccountReducer);
  const dealerId = accountReducer?.profile?.dealer_Id;

  const [currentTabValue, setCurrentTabValue] = useState(1);
  const [showRedirectionModal, setShowRedirectionModal] = useState(false);

  const { data: cart } = useActiveCart(accountReducer.isLoggedIn, false, IsImpersonationMode ? dealerId : null);
  const { labelReference, nameReference } = useLanguageReference();
  const { getInventoryItemImage } = useImage();
  const { data: inventoryItem, isLoading: gettingInventoryItem } = useInventoryItem(inventoryItemId, true);
  const [quantity, setQuantity] = useState(1);
  const { mutate: addCartItem } = usePostCarItem(accountReducer.isDemo);

  useEffect(() => {
    window.scroll({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const existingCartItem = useMemo(() => {
    if (!inventoryItem || !cart?.cartItems) return null;
    return cart.cartItems.find(ci => ci.inventoryItem_Id === inventoryItem.id);
  }, [cart, inventoryItem]);

  const quantityInCart = existingCartItem?.quantity ?? 0;

  const handleAddToCart = useCallback(
    inventoryItem => {
      const cartItem = {
        id: uuidv4(),
        inventoryItem_Id: inventoryItem.id,
        quantity: quantity,
        unitPrice: inventoryItem.price,
        currencyName: inventoryItem.currencyName,
        cart_Id: cart?.id,
        unitPricePromotion: inventoryItem.promotionPrice,
        promotionQuantity: inventoryItem.promotionQuantity,
        isInPromotion: inventoryItem.isInPromotion,
        imgSrc: inventoryItem?.images.find(i => i.dealerPortalPrimary)
          ? inventoryItem?.images.find(i => i.dealerPortalPrimary)
          : inventoryItem?.images[0],
        name: inventoryItem.name,
      };
      addCartItem(cartItem);
      setShowRedirectionModal(true);
    },
    [addCartItem, cart?.id, quantity],
  );

  const handleDownloadFile = useCallback((fileUrl, fileName) => {
    axios
      .get(fileUrl, {
        responseType: 'blob',
        headers: {
          'Content-Disposition': 'attachment',
        },
      })
      .then(response => {
        const href = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        var matches = filenameRegex.exec(response.headers['content-disposition']);
        if (matches != null && matches[1]) {
          const originalFileName = matches[1].replace(/['"]/g, '');
          const extension = originalFileName.split('.')[1];

          link.setAttribute('download', `${fileName}.${extension}`);
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      });
  }, []);

  const hasPromo = inventoryItem?.isInPromotion;

  const maxAvailableQuantity = hasPromo ? inventoryItem.promotionQuantity - quantityInCart : Infinity;

  const safeMaxAvailableQuantity = Math.max(1, maxAvailableQuantity);
  const isMaxQuantityReached = quantity >= safeMaxAvailableQuantity;

  if (!inventoryItemId) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <CartRedirectionModal
        show={showRedirectionModal}
        onClose={() => setShowRedirectionModal(false)}
        redirectLink={'/dealer/cart'}
        title={t('itemAddedToCart')}
        message={t('itemAddedToCartText')}
      />
      <GridContainer>
        <GridItem xs={12} xl={8} lg={7} md={6}>
          <ImagesSlideView
            isLoading={gettingInventoryItem}
            images={inventoryItem?.images?.filter(a => a.fileType === FileTypeEnumValue.Photo)}
            getImageUrl={imgId => getInventoryItemImage(inventoryItem?.id, imgId)}
          />
        </GridItem>
        <GridItem xs={12} xl={4} lg={5} md={6}>
          <CmtCard>
            <CmtCardContent>
              <Box display="flex" flexDirection={'column'} sx={{ mb: 2 }}>
                <SkeletonWrapper
                  skeletonProps={{
                    width: 210,
                    height: 33,
                  }}
                  isLoading={gettingInventoryItem}
                  component={<Typography variant="h1">{inventoryItem?.name}</Typography>}
                />
                <SkeletonWrapper
                  skeletonProps={{
                    width: 210,
                    height: 30,
                  }}
                  isLoading={gettingInventoryItem}
                  component={
                    <Typography variant="h5" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                      {inventoryItem?.sku}
                    </Typography>
                  }
                />
                <Divider />
              </Box>
              <Box display="flex" flexDirection={'column'}>
                <SkeletonWrapper
                  skeletonProps={{
                    width: 210,
                    height: 60,
                  }}
                  isLoading={gettingInventoryItem}
                  component={
                    <div>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}>
                        <Box>
                          <Typography variant="h2">{t('price')}</Typography>
                        </Box>
                        <Box>
                          {inventoryItem?.promotionPrice > 0 ? (
                            <>
                              <Typography fontSize={16} color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                ${inventoryItem?.price.toFixed(2)} {inventoryItem?.currencyName}
                              </Typography>
                              <Typography fontSize={25} color="error" fontWeight="bold">
                                ${inventoryItem?.promotionPrice.toFixed(2)} {inventoryItem?.currencyName}
                              </Typography>
                            </>
                          ) : (
                            <Typography fontSize={25} fontWeight="bold">
                              ${inventoryItem?.price.toFixed(2)} {inventoryItem?.currencyName}
                              {inventoryItem?.quantityPerPackage > 0 && (
                                <Typography sx={{ paddingLeft: '0.5rem' }} component="strong" color="text.secondary">
                                  ({t('quantityPerPackageInfo', { quantityPerPackage: inventoryItem?.quantityPerPackage })})
                                </Typography>
                              )}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {inventoryItem?.shortDescription && (
                        <Box>
                          <Divider sx={{ mt: 2, mb: 4 }} />
                          <Typography fontSize={14} justifyContent={'center'}>
                            {inventoryItem?.shortDescription}
                          </Typography>
                        </Box>
                      )}
                      {inventoryItem?.bundledInventoryItems?.length > 0 && (
                        <Box>
                          <Divider sx={{ mt: inventoryItem?.shortDescription ? 4 : 2, mb: 4 }} />
                          <Typography variant="h5">{t('hasBundledItems')}</Typography>
                        </Box>
                      )}

                      {inventoryItem?.bundledInventoryItems?.length === 0 && !inventoryItem?.shortDescription && (
                        <Box>
                          <Divider sx={{ mt: inventoryItem?.shortDescription ? 4 : 2, mb: 4 }} />
                        </Box>
                      )}

                      <Box sx={{ ml: 4 }}>
                        {inventoryItem?.bundledInventoryItems?.map(item => (
                          <ul>
                            <li>
                              <Typography fontSize={12} justifyContent={'center'} sx={{ mb: 1 }}>
                                {item.name || item.sku}
                              </Typography>
                            </li>
                          </ul>
                        ))}
                      </Box>
                      {inventoryItem?.bundledInventoryItems?.length > 0 && <Divider sx={{ mt: 4, mb: 4 }} />}
                    </div>
                  }
                />
                <Box
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    display: 'flex',
                    pt: 0,
                  }}>
                  <Box
                    fontSize={20}
                    style={{ width: '15vh' }}
                    sx={{ mt: 0, mb: 0 }}
                    display="flex"
                    justifyContent={'space-between'}
                    alignItems={'center'}>
                    <Fab
                      size="small"
                      color={'secondary'}
                      style={{ borderRadius: 0, width: 36, height: 36, minHeight: 36, color: '#000' }}
                      disabled={quantity <= 1}
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}>
                      <Remove />
                    </Fab>
                    <Typography>{quantity}</Typography>
                    <Fab
                      size="small"
                      color={'secondary'}
                      style={{ borderRadius: 0, width: 36, height: 36, minHeight: 36, color: '#000' }}
                      disabled={hasPromo && isMaxQuantityReached}
                      onClick={() => {
                        if (hasPromo) {
                          setQuantity(q => Math.min(safeMaxAvailableQuantity, q + 1));
                        } else {
                          setQuantity(q => q + 1);
                        }
                      }}>
                      <Add />
                    </Fab>
                  </Box>
                  <Box>
                    <Button
                      variant={'contained'}
                      color={'secondary'}
                      sx={{
                        borderRadius: 0,
                        color: '#000',
                        fontWeight: 800,
                        fontSize: 14,
                        mt: 2,
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#EFBA29',
                          boxShadow: 'none',
                        },
                      }}
                      onClick={() => handleAddToCart(inventoryItem)}
                      disabled={hasPromo && quantityInCart + quantity > inventoryItem.promotionQuantity}>
                      <Typography
                        sx={{
                          color: 'black !important',
                          fontSize: 22,
                          fontWeight: 800,
                          paddingRight: 0,
                          paddingLeft: 0,
                          paddingTop: 2,
                          paddingBottom: 2,
                          fontFamily: 'Trade Gothic Next LT Pro',
                          letterSpacing: '-0.02em',
                          mb: 1,
                        }}>
                        {t('addToCart')}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CmtCardContent>
          </CmtCard>
          <CmtCard sx={{ mt: 2, minHeight: '20vh' }}>
            <CmtCardContent style={{ height: '100%' }}>
              <Box sx={{ height: '100%' }}>
                <TabContext value={currentTabValue}>
                  <Tabs
                    className={classes.tabsList}
                    value={currentTabValue}
                    onChange={(_, value) => {
                      setCurrentTabValue(value);
                    }}
                    sx={{ height: '100%' }}
                    aria-label="profile tabs"
                    variant="scrollable"
                    allowScrollButtonsMobile>
                    <Tab className={classes.tabItem} value={1} label={t('overview')} />
                    <Tab className={classes.tabItem} value={2} label={t('installationFiles')} />
                  </Tabs>

                  <TabPanel
                    value={1}
                    sx={{
                      height: '100%',
                    }}>
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingInventoryItem}
                      component={
                        <Typography>
                          <span
                            className={classes.description}
                            dangerouslySetInnerHTML={{
                              __html: inventoryItem?.description,
                            }}
                          />
                        </Typography>
                      }
                    />
                    <Box sx={{ pt: 2, pb: 2 }}>
                      <Divider />
                    </Box>
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingInventoryItem}
                      component={
                        inventoryItem?.itemHeight || inventoryItem?.itemWidth || inventoryItem?.itemDepth ? (
                          <Typography>{`${t('dimension')}: ${t('inventoryItemDimension', {
                            width: inventoryItem?.itemWidth || 0,
                            height: inventoryItem?.itemHeight || 0,
                            depth: inventoryItem?.itemDepth || 0,
                          })}
                      ${
                        DimensionTypeEnum.find(cur => cur?.value === inventoryItem.itemDimensionTypeEnum)[labelReference]
                      }`}</Typography>
                        ) : (
                          <Typography>{`${t('dimension')}: N/A`}</Typography>
                        )
                      }
                    />
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingInventoryItem}
                      component={
                        inventoryItem?.itemWeight ? (
                          <Typography>{`${t('weight')}: ${inventoryItem?.itemWeight}Lbs`}</Typography>
                        ) : (
                          <Typography>{`${t('weight')}: N/A`}</Typography>
                        )
                      }
                    />
                  </TabPanel>

                  <TabPanel value={2}>
                    <Typography fontSize={14}>
                      {inventoryItem?.userGuideFiles?.length > 0 ? t('filesInfo') : t('noFiles')}
                    </Typography>
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingInventoryItem}
                      component={
                        inventoryItem && (
                          <Box sx={{ ml: 5, mt: 4 }}>
                            <ul>
                              {inventoryItem.userGuideFiles?.map(ugf => (
                                <li>
                                  <span
                                    onClick={() =>
                                      handleDownloadFile(getInventoryItemImage(inventoryItem.id, ugf.id), ugf[nameReference])
                                    }
                                    className={classes.fileLink}>
                                    <AttachFile color="secondary" fontSize="small" sx={{ marginBottom: '-0.3rem' }} />{' '}
                                    {ugf[nameReference]}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </Box>
                        )
                      }
                    />
                  </TabPanel>
                </TabContext>
              </Box>
            </CmtCardContent>
          </CmtCard>
        </GridItem>

        <GridItem xs={12}>
          <FrequentlyBoughtTogether
            entityId={inventoryItemId}
            frequentlyBoughtTogetherType={FrequentlyBoughtTogetherTypeEnum.Item}
          />
        </GridItem>
      </GridContainer>
    </Box>
  );
};

export default InventoryItemCategory;
